import * as angular from 'angular';
import * as _ from 'lodash';
import * as toastr from 'toastr';

import { wtDroppableDrop, seriesAdded, groupIdentifierSeparator, reconciledValues, validatedValues, ValueTypes } from '../../../common';
import { ComponentDefinition, SeriesSelectorViewModel, IdentifierType, SelectorType, ISortConfiguration, Dictionary, ISortEvent, DropObject, IValueTypeSelector, ObjectType } from '../../../models';
import { ISeriesSelectorService, ISeriesService } from '../../../services';
import { RowConfiguration } from './tableRow.configuration';

interface TableRowViewModel {
    name: string;
    viewModel: SeriesSelectorViewModel;
    serie: RowConfiguration;
    columns: {
        isDecimalsDisabled?: boolean;
        isRangeDisabled?: boolean;
        isTargetDisabled?: boolean;
    };
}

class TableRowsController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    mappedRows: TableRowViewModel[];
    rows: RowConfiguration[] = [];
    selectedSerie: RowConfiguration;
    sortConfig: ISortConfiguration<TableRowViewModel>;

    readonly formController: angular.IFormController;

    private _hasChangedValueType: boolean;
    private _selectorVm: Dictionary<SeriesSelectorViewModel> = {};
    private _queriesVm: Dictionary<SeriesSelectorViewModel> = {};

    static $inject = [
        '$scope',
        '$q',
        '$translate',
        'seriesSelectorService',
        'seriesService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _seriesService: ISeriesService
    ) {
        this.sortConfig = {
            animation: 150,
            onSort: evt => this.sort(evt),
            handle: '.drag-handle',
        };
    }

    $onInit() {
        if (!this.rows) {
            this.rows = [];
        }
        this._$scope.$on(wtDroppableDrop, (e, data) => this.onDrop(data));
        this._$scope.$on(seriesAdded, (e, data) => this.addSeries(data.guid));

        this._$scope.$watch(() => this.rows, (newRowConfigurations, oldRowConfigurations) => {
            if (newRowConfigurations) {
                //find changed rows
                const updatedRows: RowConfiguration[] = [];
                newRowConfigurations.forEach(rowConfiguration => {
                    const oldRow = oldRowConfigurations.find(old => old.guid === rowConfiguration.guid && old.value === rowConfiguration.value);
                    if (!oldRow || (oldRow && this.isConfigurationUpdated(oldRow, rowConfiguration))) {
                        updatedRows.push(rowConfiguration);
                    }
                });
                this.onRowChange(updatedRows);
            }
        }, true);

        this._$q
            .all([
                this._seriesSelectorService.getSeriesSelectorViewModels(),
                this._seriesSelectorService.getQueriesSeriesSelectorViewModels()
            ])
            .then(data => {
                data[0].forEach(s => this._selectorVm[s.guid] = s);
                data[1].forEach(q => this._queriesVm[q.guid] = q);
            })
            .finally(() => {
                if (this.rows) {
                    this.onRowChange();
                    if (this.mappedRows) {
                        this.mappedRows.forEach(mr => {
                            const r = mr.serie;
                            if (r.queryValueTypes.length === 1 && r.queryValueTypes.map(d => d.key).indexOf(r.value) < 0) {
                                r.value = r.queryValueTypes[0].key;
                                if (this.formController) {
                                    this.formController.$setDirty();
                                } else {
                                    this._hasChangedValueType = true;
                                }
                            }
                        });
                    }
                }
            });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['formController'] && this._hasChangedValueType) {
            this.formController.$setDirty();
            this._hasChangedValueType = null;
        }
    }

    onValueSelectionChange() {
        this.mappedRows?.forEach(r => this.computeColumnVisibility(r));
    }

    private isConfigurationUpdated(oldRowConfiguration: RowConfiguration, newRowConfiguration: RowConfiguration) {
        return !this.areRowConfigurationEquals(oldRowConfiguration, newRowConfiguration);
    }

    private areRowConfigurationEquals(oldConfiguration: RowConfiguration, newConfiguration: RowConfiguration) {
        let areEqual: boolean = false;
        if (oldConfiguration && newConfiguration) {
            if (oldConfiguration.color === newConfiguration.color
                && oldConfiguration.decimals === newConfiguration.decimals
                && oldConfiguration.guid === newConfiguration.guid
                && oldConfiguration.hasReconciledValue === newConfiguration.hasReconciledValue
                && oldConfiguration.isAlarmVisible === newConfiguration.isAlarmVisible
                && oldConfiguration.isBoundIncludehildren === newConfiguration.isBoundIncludehildren
                && oldConfiguration.isBoundToGroup === newConfiguration.isBoundToGroup
                && oldConfiguration.isCommentVisible === newConfiguration.isCommentVisible
                && oldConfiguration.isConstraint === newConfiguration.isConstraint
                && oldConfiguration.isNameVisble === newConfiguration.isNameVisble
                && oldConfiguration.isQuery === newConfiguration.isQuery
                && oldConfiguration.isRangeVisible === newConfiguration.isRangeVisible
                && oldConfiguration.isTargetVisible === newConfiguration.isTargetVisible
                && oldConfiguration.isUnitVisible === newConfiguration.isUnitVisible
                && oldConfiguration.link === newConfiguration.link
                && oldConfiguration.originalGuid === newConfiguration.originalGuid
                && oldConfiguration.selectorType === newConfiguration.selectorType
                && oldConfiguration.sortOrder === newConfiguration.sortOrder
                && oldConfiguration.value === newConfiguration.value) {
                let areQueryValueTypesEquals = false;
                if (!oldConfiguration.queryValueTypes && !newConfiguration.queryValueTypes) {
                    areQueryValueTypesEquals = true;
                } else {
                    areQueryValueTypesEquals = true;
                    oldConfiguration.queryValueTypes.forEach(qvt => {
                        if (!newConfiguration.queryValueTypes.find(oqvt => oqvt.key === qvt.key && oqvt.value === qvt.value) && areQueryValueTypesEquals) {
                            areQueryValueTypesEquals = false;
                        }
                    });
                }
                areEqual = areQueryValueTypesEquals;
            }
        } else if (!oldConfiguration && !newConfiguration) {
            areEqual = true;
        }
        return areEqual;
    }

    private computeColumnVisibility(row: TableRowViewModel) {
        const isComment = row.serie.value === ValueTypes.comment.key;
        row.columns.isDecimalsDisabled = isComment;
        row.columns.isRangeDisabled = isComment;
        row.columns.isTargetDisabled = isComment;
    }

    private onRowChange(updatedRowConfigurations?: RowConfiguration[]) {
        if (updatedRowConfigurations && this.mappedRows) {
            updatedRowConfigurations.forEach(updatedRow => {
                const foundRow = this.mappedRows.find(r => r.serie.guid === updatedRow.guid);
                if (foundRow) {
                    foundRow.serie = updatedRow;
                    const vm = this.reassignNames(this.getSerie(updatedRow.guid), updatedRow);
                    this.computeColumnVisibility(vm);
                    if (updatedRow.isQuery && vm && vm.viewModel) {
                        updatedRow.queryValueTypes = this._seriesSelectorService.getValues(vm.viewModel as IValueTypeSelector, false, updatedRow.isQuery);
                    }
                }
            });
        }
        this.mappedRows = this.rows.map(r => {
            const vm = this.reassignNames(this.getSerie(r.guid), r);
            this.computeColumnVisibility(vm);
            if (r.isQuery && vm && vm.viewModel) {
                r.queryValueTypes = this._seriesSelectorService.getValues(vm.viewModel as IValueTypeSelector, false, r.isQuery);
            }
            return vm;
        });
    }

    private reassignNames(s: SeriesSelectorViewModel, r: RowConfiguration): TableRowViewModel {
        const seriesName = s && s.name
            ? s.name
            : '';
        const name = s && s.groupUniqueName
            ? s.groupUniqueName + groupIdentifierSeparator + s.name
            : seriesName;

        return {
            name: name,
            viewModel: s,
            serie: r,
            columns: {}
        };
    }

    private onDrop(data: DropObject) {
        if (data.itemId) {
            const guid = data.itemId.replace('id', '');
            if (data.binId) {
                const bin = data.binId.split('+');
                this.updateSerie(bin[0], bin[1], guid);
            } else {
                this.addSeries(guid);
            }
        }
    }

    private getValueType(serie: RowConfiguration, svm: SeriesSelectorViewModel): string {
        const availableValueTypes = this._seriesSelectorService.getValues(svm, false, serie.isQuery);
        let result = serie.value;
        if (serie.isQuery && availableValueTypes.length > 0
            || availableValueTypes && availableValueTypes.length > 0) {
            result = availableValueTypes[0].key;
        }
        if (serie.hasReconciledValue) {
            result = reconciledValues[0].key;
        } else if (svm.identifierType === IdentifierType.Validated) {
            result = validatedValues[0].key;
        }

        return result;
    }

    private addSeries(guid: string): void {
        const row = new RowConfiguration();
        row.guid = guid;
        row.sortOrder = 0;
        const maxRow = _.maxBy(this.rows, r => r.sortOrder);
        if (maxRow) {
            row.sortOrder = maxRow.sortOrder + 1;
        } else {
            row.sortOrder = this.rows.length;
        }
        const vm = this.getSerie(guid);
        row.isQuery = vm.type === SelectorType.query;
        if (vm) {
            row.selectorType = vm.type;
            row.hasReconciledValue = vm.hasReconciledValue;
            row.isConstraint = vm.isConstraint;
            row.value = this.getValueType(row, vm);
            if (row.isQuery) {
                row.queryValueTypes = this._seriesSelectorService.getValues(vm as IValueTypeSelector, false, row.isQuery);
            }
        }
        this._seriesService.bind(row, vm.groupAbsolutePath);
        const rows = angular.copy(this.rows);
        rows.push(row);
        this.rows = rows;
    }

    private updateSerie(oldValue: string, oldGuid: string, newGuid: string) {
        const svm = this.getSerie(newGuid);
        const old: RowConfiguration = _.find(this.rows, s => s.guid === oldGuid && s.value === oldValue);

        let newValueType: string;
        let isQuery = false;
        if (svm.type === SelectorType.query) {
            isQuery = true;
            newValueType = this._seriesSelectorService.getAvailableValueTypeFromQuery(svm, this.rows, old);
        } else {
            newValueType = this._seriesSelectorService.getAvailableValueType(svm, this.rows, old);
        }

        if (isQuery !== old.isQuery) {
            toastr.warning(this._$translate.instant('Cannot convert between query and series'));
        } else {
            if (newValueType || isQuery) {
                old.guid = newGuid;
                old.value = newValueType;
                old.selectorType = this.objectTypeToSelectorType(svm.objectType);
                old.isBoundToGroup = svm.type === SelectorType.identifier
                    ? old.isBoundToGroup
                    : false;
                old.isQuery = isQuery;
            }
        }
    }

    private objectTypeToSelectorType(objectType: ObjectType): SelectorType {
        let selectorType: SelectorType;
        switch (objectType) {
            case ObjectType.Tag:
                selectorType = SelectorType.tagDefinition;
                break;
            case ObjectType.Identifier:
                selectorType = SelectorType.identifier;
                break;
            case ObjectType.Undefined:
                selectorType = SelectorType.constant;
                break;
            case ObjectType.HistorianTag:
                selectorType = SelectorType.historianTag;
                break;
        }
        return selectorType;
    }

    private sort(evt: ISortEvent<TableRowViewModel>) {
        evt.models.forEach((s, i) => s.serie.sortOrder = i);
        this.rows = evt.models.map(d => d.serie);
    }

    private getSerie(guid: string): SeriesSelectorViewModel {
        const result = this._selectorVm[guid];
        return result
            ? result
            : this._queriesVm[guid];
    }

    deleteSeries(row: RowConfiguration) {
        _.remove(this.rows, r => r.guid === row.guid && r.value === row.value);
    }

    select(serie): void {
        this.selectedSerie = serie;
    }
}

export const wtTableRows: ComponentDefinition = {
    componentId: 'wtTableRows',
    controller: TableRowsController,
    template: require('./tableRows.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '<?',
        isBindable: '=',
        rows: '='
    }
};